import { useEffect, useState, useMemo } from 'react';
import PlayerNewsList from '@/components/player/PlayerNewsList';
import { format, parseISO } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { useTrashRankings } from '@/hooks/useTrashRankings';
import { useScoringRules } from '@/contexts/ScoringRulesContext';
import { ScoringRule } from '@/types/scoring';
import { useAddDrop } from '@/hooks/useAddDrop';
import { useWaiverClaims } from '@/hooks/useWaiverClaims';
import { trackEvent } from '@/lib/analytics';
import { useLeague } from '@/contexts/LeagueContext';
import AddDropDialog, { type WaiverMode } from '@/components/mlb-beta/AddDropDialog';
import {
  Crown, Skull, TrendingUp, TrendingDown, ArrowLeft, Flame, Trophy,
  UserPlus, UserMinus, ArrowLeftRight, BarChart3, Newspaper, Calendar,
  Activity, Zap
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { buildScoringWeights, calculateTrashPoints } from '@/lib/trashScoring';
import { formatPositionDisplay } from '@/lib/positionEligibility';
import { MLB_TEAM_ID_TO_ABBR } from '@/data/mlbTeams';

interface Props {
  playerId: string;
  onBack: () => void;
  onNavigateToTrades?: () => void;
}

interface GameStat {
  id: string;
  game_date: string;
  game_pk: number;
  trash_points: number;
  strikeouts: number;
  errors: number;
  gidp: number;
  earned_runs: number;
  blown_saves: number;
  wild_pitches: number;
  hits: number;
  home_runs: number;
  rbi: number;
  stolen_bases: number;
  pitcher_strikeouts: number;
  pitcher_wins: number;
  saves: number;
  is_pitcher: boolean;
  at_bats: number | null;
  innings_pitched: number | null;
  walks_allowed: number;
  runs_scored: number;
  walks_drawn: number;
  doubles: number;
  triples: number;
  hit_batsmen: number;
  home_runs_allowed: number;
  balks: number;
  passed_balls: number;
  caught_stealing: number;
  outfield_assists: number;
}

interface PlayerInfo {
  id: string;
  full_name: string;
  position_abbreviation: string;
  position_eligibility: string[] | null;
  position_type: string;
  mlb_team_name: string | null;
  mlb_team_abbreviation: string | null;
  headshot_url: string | null;
  jersey_number: string | null;
  status: string;
}

interface LeagueActivityItem {
  id: string;
  activity_type: string;
  player_name: string | null;
  details: any;
  created_at: string;
  team_id: string;
}

const MLBBetaPlayerDetail = ({ playerId, onBack, onNavigateToTrades }: Props) => {
  // SAB-1173: tab-badge count, reported by PlayerNewsList rather than a second query.
  const [newsCount, setNewsCount] = useState(0);
  const [player, setPlayer] = useState<PlayerInfo | null>(null);
  const [gameStats, setGameStats] = useState<GameStat[]>([]);
  const [seasonStats, setSeasonStats] = useState<any>(null);
  const [activity, setActivity] = useState<LeagueActivityItem[]>([]);
  const [gameOpponents, setGameOpponents] = useState<Map<string, { opponent: string; isHome: boolean }>>(new Map());
  const [loading, setLoading] = useState(true);
  const [detailStatView, setDetailStatView] = useState<'season' | 'pergame'>('season');

  const { scoringRules } = useScoringRules();
  const { rankings } = useTrashRankings(scoringRules, 2026, true);
  const addDrop = useAddDrop();
  const waiverClaims = useWaiverClaims();
  const { activeLeague } = useLeague();
  const waiverMode = (activeLeague?.waiver_type as WaiverMode) || 'fcfs';
  const [addDropOpen, setAddDropOpen] = useState(false);

  // Find this player in rankings
  const ranked = useMemo(() => rankings.find(r => r.player_id === playerId), [rankings, playerId]);

  // Ownership info
  const ownerTeamName = addDrop.getOwnerTeamName(playerId);
  const isOnMyTeam = addDrop.isOnMyTeam(playerId);
  const isFreeAgent = addDrop.isFreeAgent(playerId);

  // Calculate ownership % (how many leagues own this player — simplified as owned/not for single league)
  const ownershipPct = isFreeAgent ? 0 : 100;

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);

      // Fetch player info + projected pts
      const { data: pData } = await supabase
        .from('players')
        .select('id, full_name, position_abbreviation, position_eligibility, position_type, mlb_team_name, mlb_team_abbreviation, headshot_url, jersey_number, status')
        .eq('id', playerId)
        .single();

      if (pData) {
        setPlayer(pData as PlayerInfo);
      }

      // Fetch season stats
      const { data: ssData } = await supabase
        .from('player_season_stats')
        .select('*')
        .eq('player_id', playerId)
        .order('season', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (ssData) setSeasonStats(ssData);

      // Fetch recent game stats (last 20 games)
      const { data: gData } = await supabase
        .from('player_game_stats')
        .select('*')
        .eq('player_id', playerId)
        .order('game_date', { ascending: false })
        .limit(20);

      if (gData) {
        // Compute per-game SAB PTS from raw game stats + league scoring weights
        const weights = buildScoringWeights(activeLeague?.category_config);
        const withScores = (gData as GameStat[]).map(g => ({
          ...g,
          trash_points: calculateTrashPoints(g as unknown as Record<string, number>, weights),
        }));
        setGameStats(withScores);
        // Fetch opponent info from game_schedule for game log
        if (pData?.mlb_team_name && gData.length > 0) {
          const gameDates = [...new Set(gData.map(g => g.game_date))];
          const { data: schedData } = await supabase
            .from('game_schedule')
            .select('game_date, home_team_name, away_team_name, home_team_id, away_team_id')
            .in('game_date', gameDates)
            .or(`home_team_name.eq.${pData.mlb_team_name},away_team_name.eq.${pData.mlb_team_name}`);
          if (schedData) {
            const oppMap = new Map<string, { opponent: string; isHome: boolean }>();
            for (const g of schedData) {
              const isHome = g.home_team_name === pData.mlb_team_name;
              const oppId = isHome ? g.away_team_id : g.home_team_id;
              const oppAbbr = MLB_TEAM_ID_TO_ABBR[oppId] || '??';
              oppMap.set(g.game_date, { opponent: oppAbbr, isHome });
            }
            setGameOpponents(oppMap);
          }
        }
      }

      // SAB-1173: news is fetched by PlayerNewsList now.

      // Fetch add/drop activity for this player
      if (activeLeague?.id) {
        const { data: aData } = await supabase
          .from('league_activity')
          .select('*')
          .eq('league_id', activeLeague.id)
          .eq('player_id', playerId)
          .order('created_at', { ascending: false })
          .limit(10);

        if (aData) setActivity(aData as LeagueActivityItem[]);
      }

      setLoading(false);
    };

    fetchAll();
  }, [playerId, activeLeague?.id]);

  // Determine pitcher status from ranked data or season stats
  const isPitcher = ranked?.is_pitcher ?? seasonStats?.is_pitcher ?? false;

  // Calculate trash/anti stats from scoring rules for display — filtered by player type
  const playerRules = useMemo(() => {
    return filterRulesForPlayer(scoringRules, isPitcher);
  }, [scoringRules, isPitcher]);

  const trashStats = useMemo(() => {
    if (!seasonStats) return [];
    return playerRules
      .filter(r => r.isEnabled && !r.isNegative)
      .map(rule => {
        const statKey = getStatValue(seasonStats, rule.stat);
        return { label: rule.label, value: statKey, points: statKey * rule.pointValue, icon: '💩' };
      })
      .filter(s => s.value > 0)
      .sort((a, b) => b.points - a.points);
  }, [seasonStats, playerRules]);

  const antiStats = useMemo(() => {
    if (!seasonStats) return [];
    return playerRules
      .filter(r => r.isEnabled && r.isNegative)
      .map(rule => {
        const statKey = getStatValue(seasonStats, rule.stat);
        return { label: rule.label, value: statKey, points: statKey * rule.pointValue, icon: '⚡' };
      })
      .filter(s => s.value > 0)
      .sort((a, b) => a.points - b.points); // most negative first
  }, [seasonStats, playerRules]);

  // Compute TRASH/ANTI/NET PTS from seasonStats + scoringRules (reliable, not dependent on player_match_scores coverage)
  const computedTrashPts = useMemo(() => trashStats.reduce((sum, s) => sum + s.points, 0), [trashStats]);
  const computedAntiPts = useMemo(() => antiStats.reduce((sum, s) => sum + s.points, 0), [antiStats]);
  const computedNetPts = computedTrashPts + computedAntiPts;

  // Fielding stats from season stats
  const fieldingStats = useMemo(() => {
    if (!seasonStats) return [];
    const fields = [
      { label: 'Errors', value: seasonStats.errors || 0 },
      { label: 'Passed Balls', value: seasonStats.passed_balls || 0 },
      ...(!isPitcher ? [{ label: 'Outfield Assists', value: seasonStats.outfield_assists || 0 }] : []),
    ];
    return fields.filter(f => f.value > 0);
  }, [seasonStats, isPitcher]);

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-40" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (!player) {
    return (
      <div className="text-center py-10">
        <p className="text-muted-foreground">Player not found</p>
        <Button variant="ghost" onClick={onBack} className="mt-2">Go Back</Button>
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-20 md:pb-0">
      {/* Back button */}
      <Button variant="ghost" size="sm" onClick={onBack} className="gap-1 -ml-2 text-primary">
        <ArrowLeft className="w-4 h-4" /> Back to Players
      </Button>

      {/* Hero Card */}
      <Card className="overflow-hidden border-2 border-primary/20">
        <div className="bg-gradient-to-r from-primary/15 via-primary/5 to-transparent p-5">
          <div className="flex items-start gap-4">
            {/* Headshot */}
            <div className="relative flex-shrink-0">
              {player.headshot_url ? (
                <img
                  src={player.headshot_url}
                  alt={player.full_name}
                  className="w-20 h-20 rounded-xl bg-muted object-cover border-2 border-primary/20"
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
              ) : (
                <div className="w-20 h-20 rounded-xl bg-primary/10 flex items-center justify-center text-2xl font-bold text-primary">
                  {player.full_name.split(' ').map(n => n[0]).join('')}
                </div>
              )}
              {ranked && ranked.rank <= 10 && (
                <div className="absolute -top-2 -right-2 bg-gold text-background rounded-full w-7 h-7 flex items-center justify-center text-xs font-black">
                  #{ranked.rank}
                </div>
              )}
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <h1 className="text-xl font-bold font-heading text-foreground truncate">{player.full_name}</h1>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <Badge variant="outline" className="text-xs">{formatPositionDisplay(player.position_abbreviation, player.position_eligibility)}</Badge>
                <span className="text-sm text-muted-foreground">{player.mlb_team_abbreviation || player.mlb_team_name || 'No MLB Team'}</span>
                {player.jersey_number && (
                  <span className="text-sm text-muted-foreground">#{player.jersey_number}</span>
                )}
              </div>
              <div className="flex items-center gap-2 mt-2">
                {isFreeAgent ? (
                  <Badge variant="outline" className="text-xs text-success border-success/30">🟢 Free Agent</Badge>
                ) : isOnMyTeam ? (
                  <Badge variant="secondary" className="text-xs bg-primary/15 text-primary border-primary/20">My Team</Badge>
                ) : (
                  <Badge variant="outline" className="text-xs">{ownerTeamName}</Badge>
                )}
              </div>
            </div>

            {/* Big trash score */}
            <div className="text-right flex-shrink-0">
              {seasonStats ? (
                <>
                  <p className={cn(
                    "text-3xl font-black font-heading",
                    computedNetPts >= 0 ? 'text-success' : 'text-destructive'
                  )}>
                    {computedNetPts.toFixed(1)}
                  </p>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Trash Pts</p>
                  {ranked && (
                    <div className="flex items-center justify-end gap-1 mt-1">
                      {ranked.rank <= 3 ? <Crown className="w-3.5 h-3.5 text-gold" /> : <Skull className="w-3.5 h-3.5 text-primary" />}
                      <span className="text-xs font-bold text-muted-foreground">Rank #{ranked.rank}</span>
                    </div>
                  )}
                </>
              ) : (
                <p className="text-sm text-muted-foreground">No stats</p>
              )}
            </div>
          </div>

          {/* Quick action */}
          <div className="flex gap-2 mt-4">
            {isFreeAgent && addDrop.myTeam && (
              <Button size="sm" className="gap-1.5 text-xs" onClick={() => setAddDropOpen(true)}>
                <UserPlus className="w-3.5 h-3.5" /> Add Player
              </Button>
            )}
            {isOnMyTeam && (
              <Button size="sm" variant="destructive" className="gap-1.5 text-xs" onClick={() => setAddDropOpen(true)}>
                <UserMinus className="w-3.5 h-3.5" /> Drop Player
              </Button>
            )}
            {!isFreeAgent && !isOnMyTeam && (
              <Button size="sm" variant="outline" className="gap-1.5 text-xs" onClick={onNavigateToTrades}>
                <ArrowLeftRight className="w-3.5 h-3.5" /> Propose Trade
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* Score Breakdown Summary */}
      {seasonStats && (
        <div className="grid grid-cols-3 gap-2">
          <Card className="bg-success/5 border-success/20">
            <CardContent className="p-3 text-center">
              <p className="text-lg font-black text-success">{computedTrashPts.toFixed(1)}</p>
              <p className="text-[10px] text-muted-foreground uppercase">Trash 💩</p>
            </CardContent>
          </Card>
          <Card className="bg-destructive/5 border-destructive/20">
            <CardContent className="p-3 text-center">
              <p className="text-lg font-black text-destructive">{computedAntiPts.toFixed(1)}</p>
              <p className="text-[10px] text-muted-foreground uppercase">Anti ⚡</p>
            </CardContent>
          </Card>
          <Card className="bg-muted/30">
            <CardContent className="p-3 text-center">
              <p className="text-lg font-black text-foreground">{seasonStats?.games_played || 0}</p>
              <p className="text-[10px] text-muted-foreground uppercase">Games</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs defaultValue="stats" className="flex-1">
        <TabsList className="w-full justify-start bg-muted/50 h-10">
          <TabsTrigger value="stats" className="gap-1.5 text-xs data-[state=active]:bg-primary/10">
            <BarChart3 className="w-3.5 h-3.5" /> Stats
          </TabsTrigger>
          <TabsTrigger value="news" className="gap-1.5 text-xs data-[state=active]:bg-primary/10">
            <Newspaper className="w-3.5 h-3.5" /> News {newsCount > 0 && <Badge variant="secondary" className="text-[9px] ml-0.5 px-1 py-0 h-4">{newsCount}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="gamelog" className="gap-1.5 text-xs data-[state=active]:bg-primary/10">
            <Calendar className="w-3.5 h-3.5" /> Game Log
          </TabsTrigger>
          <TabsTrigger value="activity" className="gap-1.5 text-xs data-[state=active]:bg-primary/10">
            <Activity className="w-3.5 h-3.5" /> Activity
          </TabsTrigger>
        </TabsList>

        {/* Stats Tab */}
        <TabsContent value="stats" className="space-y-4 mt-4">
          {/* 2026 Projections / Season Stats Table */}
          {seasonStats ? (() => {
            const s = seasonStats;
            const gp = s.games_played || 0;
            const isPitcher = s.is_pitcher;
            const perGame = detailStatView === 'pergame';
            const pg = (v: number) => gp > 0 ? (v / gp).toFixed(1) : '-';
            const fmtI = (v: number) => v === 0 ? '-' : String(v);
            const fmtA = (v: number) => v === 0 ? '-' : v.toFixed(3).replace(/^0/, '');
            const fmtR = (v: number) => v === 0 ? '-' : v.toFixed(2);

            const batterRows = [
              { label: 'GP', value: fmtI(gp) },
              { label: 'H', value: perGame ? pg(s.hits) : fmtI(s.hits) },
              { label: 'R', value: perGame ? pg(s.runs_scored) : fmtI(s.runs_scored) },
              { label: '1B', value: perGame ? pg(s.hits - s.doubles - s.triples - s.home_runs) : fmtI(s.hits - s.doubles - s.triples - s.home_runs) },
              { label: '2B', value: perGame ? pg(s.doubles) : fmtI(s.doubles) },
              { label: '3B', value: perGame ? pg(s.triples) : fmtI(s.triples) },
              { label: 'HR', value: perGame ? pg(s.home_runs) : fmtI(s.home_runs) },
              { label: 'RBI', value: perGame ? pg(s.rbi) : fmtI(s.rbi) },
              { label: 'BB', value: perGame ? pg(s.walks_drawn) : fmtI(s.walks_drawn) },
              { label: 'K', value: perGame ? pg(s.strikeouts) : fmtI(s.strikeouts) },
              { label: 'SB', value: perGame ? pg(s.stolen_bases) : fmtI(s.stolen_bases) },
              { label: 'CS', value: perGame ? pg(s.caught_stealing) : fmtI(s.caught_stealing) },
              { label: 'GIDP', value: perGame ? pg(s.gidp) : fmtI(s.gidp) },
              { label: 'AVG', value: s.at_bats > 0 ? fmtA(s.hits / s.at_bats) : '-' },
              { label: 'OBP', value: (s.at_bats + s.walks_drawn) > 0 ? fmtA((s.hits + s.walks_drawn) / (s.at_bats + s.walks_drawn)) : '-' },
              { label: 'SLG', value: s.at_bats > 0 ? fmtA(((s.hits - s.doubles - s.triples - s.home_runs) + s.doubles * 2 + s.triples * 3 + s.home_runs * 4) / s.at_bats) : '-' },
            ];

            const pitcherRows = [
              { label: 'GP', value: fmtI(gp) },
              { label: 'W', value: perGame ? pg(s.pitcher_wins) : fmtI(s.pitcher_wins) },
              { label: 'L', value: '--' },
              { label: 'ERA', value: s.innings_pitched > 0 ? fmtR((s.earned_runs / s.innings_pitched) * 9) : '-' },
              { label: 'WHIP', value: s.innings_pitched > 0 ? fmtR((s.walks_allowed + (s.hits_allowed || 0)) / s.innings_pitched) : '-' },
              { label: 'IP', value: perGame ? pg(s.innings_pitched) : s.innings_pitched > 0 ? s.innings_pitched.toFixed(1) : '-' },
              { label: 'K', value: perGame ? pg(s.pitcher_strikeouts) : fmtI(s.pitcher_strikeouts) },
              { label: 'BB', value: perGame ? pg(s.walks_allowed) : fmtI(s.walks_allowed) },
              { label: 'HR', value: perGame ? pg(s.home_runs_allowed) : fmtI(s.home_runs_allowed) },
              { label: 'QS', value: '--' },
              { label: 'SV', value: perGame ? pg(s.saves) : fmtI(s.saves) },
              { label: 'HLD', value: '--' },
            ];

            const rows = isPitcher ? pitcherRows : batterRows;

            return (
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <BarChart3 className="w-4 h-4 text-primary" />
                      2026 {isPitcher ? 'Pitching' : 'Batting'} Stats
                    </CardTitle>
                    <div className="flex items-center gap-1">
                      <Button
                        size="sm"
                        variant={detailStatView === 'season' ? 'secondary' : 'ghost'}
                        className="h-6 text-[10px] px-2"
                        onClick={() => setDetailStatView('season')}
                      >
                        Season
                      </Button>
                      <Button
                        size="sm"
                        variant={detailStatView === 'pergame' ? 'secondary' : 'ghost'}
                        className="h-6 text-[10px] px-2"
                        onClick={() => setDetailStatView('pergame')}
                      >
                        Per Game
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-muted/50 text-muted-foreground">
                          {rows.map(r => (
                            <th key={r.label} className="text-center px-2 py-1.5 font-semibold whitespace-nowrap">{r.label}</th>
                          ))}
                          <th className="text-center px-2 py-1.5 font-bold text-orange-400 whitespace-nowrap">SAB PTS</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-t border-border/50">
                          {rows.map(r => (
                            <td key={r.label} className="text-center px-2 py-2 text-foreground tabular-nums">{r.value}</td>
                          ))}
                          <td className="text-center px-2 py-2 font-bold text-orange-400 tabular-nums">
                            {gp > 0
                              ? (perGame ? (computedNetPts / gp).toFixed(1) : computedNetPts.toFixed(1))
                              : '-'}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            );
          })() : null}

          {/* Stat Splits (stub) */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Zap className="w-4 h-4 text-primary" />
                Stat Splits
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-muted/50 text-muted-foreground">
                      <th className="text-left px-3 py-1.5 font-semibold">Split</th>
                      <th className="text-center px-2 py-1.5 font-semibold">GP</th>
                      <th className="text-center px-2 py-1.5 font-semibold">AVG</th>
                      <th className="text-center px-2 py-1.5 font-semibold">HR</th>
                      <th className="text-center px-2 py-1.5 font-semibold">RBI</th>
                      <th className="text-center px-2 py-1.5 font-bold text-orange-400">SAB PTS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {['vs LHP', 'vs RHP', 'Home', 'Away'].map(split => (
                      <tr key={split} className="border-t border-border/50">
                        <td className="px-3 py-2 text-foreground font-medium">{split}</td>
                        <td className="text-center px-2 py-2 text-muted-foreground">--</td>
                        <td className="text-center px-2 py-2 text-muted-foreground">--</td>
                        <td className="text-center px-2 py-2 text-muted-foreground">--</td>
                        <td className="text-center px-2 py-2 text-muted-foreground">--</td>
                        <td className="text-center px-2 py-2 text-muted-foreground">--</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Trash Stats */}
          {trashStats.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <span className="text-base">💩</span> Trash Stats
                  <Badge variant="secondary" className="text-[10px] ml-auto bg-success/10 text-success">
                    +{computedTrashPts.toFixed(1)} pts
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-1">
                {trashStats.map((stat, i) => (
                  <div key={i} className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-muted/30 transition-colors">
                    <span className="text-sm text-foreground">{stat.label}</span>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-muted-foreground">{stat.value}</span>
                      <span className="text-sm font-bold text-success min-w-[50px] text-right">+{stat.points.toFixed(1)}</span>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Anti Stats */}
          {antiStats.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <span className="text-base">⚡</span> Anti-Stats
                  <Badge variant="secondary" className="text-[10px] ml-auto bg-destructive/10 text-destructive">
                    {computedAntiPts.toFixed(1)} pts
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-1">
                {antiStats.map((stat, i) => (
                  <div key={i} className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-muted/30 transition-colors">
                    <span className="text-sm text-foreground">{stat.label}</span>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-muted-foreground">{stat.value}</span>
                      <span className="text-sm font-bold text-destructive min-w-[50px] text-right">{stat.points.toFixed(1)}</span>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Fielding Stats */}
          {fieldingStats.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <span className="text-base">🧤</span> Fielding Stats
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-1">
                {fieldingStats.map((stat, i) => (
                  <div key={i} className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-muted/30 transition-colors">
                    <span className="text-sm text-foreground">{stat.label}</span>
                    <span className="text-sm text-muted-foreground">{stat.value}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {!ranked && !seasonStats && (
            <Card className="border-dashed border-2">
              <CardContent className="py-8 text-center">
                <Skull className="w-8 h-8 mx-auto text-muted-foreground/40 mb-2" />
                <p className="text-sm text-muted-foreground">No season stats available yet</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* News Tab */}
        {/* SAB-1173: the shared list. This tab used to carry its own copy of the
            markup, and MLBBetaPlayerDetail carried a second one keyed differently
            against the same table. Three copies was the trajectory; there is one. */}
        <TabsContent value="news" className="mt-4 space-y-2">
          <PlayerNewsList sport="mlb" playerId={playerId} onLoaded={setNewsCount} />
        </TabsContent>

        {/* Game Log Tab */}
        <TabsContent value="gamelog" className="mt-4">
          {gameStats.length === 0 ? (
            <Card className="border-dashed border-2">
              <CardContent className="py-8 text-center">
                <Calendar className="w-8 h-8 mx-auto text-muted-foreground/40 mb-2" />
                <p className="text-sm text-muted-foreground">No game data yet</p>
              </CardContent>
            </Card>
          ) : (
            <div className="rounded-lg border border-border overflow-hidden overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/50 text-muted-foreground text-xs">
                    <th className="text-left px-3 py-2">Date</th>
                    <th className="text-left px-3 py-2">OPP</th>
                    <th className="text-right px-3 py-2">Trash</th>
                    {isPitcher ? (
                      <>
                        <th className="text-center px-2 py-2 hidden sm:table-cell">IP</th>
                        <th className="text-center px-2 py-2 hidden sm:table-cell">K</th>
                        <th className="text-center px-2 py-2 hidden sm:table-cell">ER</th>
                        <th className="text-center px-2 py-2 hidden sm:table-cell">H</th>
                        <th className="text-center px-2 py-2 hidden sm:table-cell">BB</th>
                        <th className="text-center px-2 py-2 hidden sm:table-cell">HR</th>
                        <th className="text-center px-2 py-2 hidden sm:table-cell">W</th>
                        <th className="text-center px-2 py-2 hidden sm:table-cell">SV</th>
                      </>
                    ) : (
                      <>
                        <th className="text-center px-2 py-2 hidden sm:table-cell">H</th>
                        <th className="text-center px-2 py-2 hidden sm:table-cell">HR</th>
                        <th className="text-center px-2 py-2 hidden sm:table-cell">RBI</th>
                        <th className="text-center px-2 py-2 hidden sm:table-cell">R</th>
                        <th className="text-center px-2 py-2 hidden sm:table-cell">K</th>
                        <th className="text-center px-2 py-2 hidden sm:table-cell">BB</th>
                        <th className="text-center px-2 py-2 hidden sm:table-cell">SB</th>
                        <th className="text-center px-2 py-2 hidden sm:table-cell">E</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {gameStats.map((game) => {
                    const opp = gameOpponents.get(game.game_date);
                    return (
                      <tr key={game.id} className="border-t border-border/50 hover:bg-muted/20 transition-colors">
                        <td className="px-3 py-2.5 text-foreground font-medium whitespace-nowrap">
                          {/* SAB-412: parseISO parses bare YYYY-MM-DD as local date,
                              not UTC. game_date is a `date` column (no time/zone), so
                              `new Date(game_date)` was rendering 1 day earlier for all
                              users west of UTC. */}
                          {format(parseISO(game.game_date), 'MMM d')}
                        </td>
                        <td className="px-3 py-2.5 text-muted-foreground text-xs whitespace-nowrap">
                          {opp ? `${opp.isHome ? 'vs' : '@'} ${opp.opponent}` : '-'}
                        </td>
                        <td className="px-3 py-2.5 text-right">
                          <span className={cn(
                            'font-bold',
                            game.trash_points >= 5 ? 'text-success' : game.trash_points >= 0 ? 'text-foreground' : 'text-destructive'
                          )}>
                            {Number(game.trash_points).toFixed(1)}
                            {Number(game.trash_points) >= 10 && <Flame className="w-3 h-3 inline ml-0.5 text-orange-500" />}
                          </span>
                        </td>
                        {isPitcher ? (
                          <>
                            <td className="px-2 py-2.5 text-center hidden sm:table-cell text-muted-foreground">{game.innings_pitched != null ? Number(game.innings_pitched).toFixed(1) : '-'}</td>
                            <td className="px-2 py-2.5 text-center hidden sm:table-cell text-muted-foreground">{game.pitcher_strikeouts || '-'}</td>
                            <td className="px-2 py-2.5 text-center hidden sm:table-cell text-muted-foreground">{game.earned_runs || '-'}</td>
                            <td className="px-2 py-2.5 text-center hidden sm:table-cell text-muted-foreground">{game.hits || '-'}</td>
                            <td className="px-2 py-2.5 text-center hidden sm:table-cell text-muted-foreground">{game.walks_allowed || '-'}</td>
                            <td className="px-2 py-2.5 text-center hidden sm:table-cell text-muted-foreground">{game.home_runs_allowed || '-'}</td>
                            <td className="px-2 py-2.5 text-center hidden sm:table-cell text-muted-foreground">{game.pitcher_wins || '-'}</td>
                            <td className="px-2 py-2.5 text-center hidden sm:table-cell text-muted-foreground">{game.saves || '-'}</td>
                          </>
                        ) : (
                          <>
                            <td className="px-2 py-2.5 text-center hidden sm:table-cell text-muted-foreground">{game.hits || '-'}</td>
                            <td className="px-2 py-2.5 text-center hidden sm:table-cell text-muted-foreground">{game.home_runs || '-'}</td>
                            <td className="px-2 py-2.5 text-center hidden sm:table-cell text-muted-foreground">{game.rbi || '-'}</td>
                            <td className="px-2 py-2.5 text-center hidden sm:table-cell text-muted-foreground">{game.runs_scored || '-'}</td>
                            <td className="px-2 py-2.5 text-center hidden sm:table-cell text-muted-foreground">{game.strikeouts || '-'}</td>
                            <td className="px-2 py-2.5 text-center hidden sm:table-cell text-muted-foreground">{game.walks_drawn || '-'}</td>
                            <td className="px-2 py-2.5 text-center hidden sm:table-cell text-muted-foreground">{game.stolen_bases || '-'}</td>
                            <td className="px-2 py-2.5 text-center hidden sm:table-cell text-muted-foreground">{game.errors || '-'}</td>
                          </>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        {/* Activity Tab */}
        <TabsContent value="activity" className="mt-4 space-y-2">
          {activity.length === 0 ? (
            <Card className="border-dashed border-2">
              <CardContent className="py-8 text-center">
                <Activity className="w-8 h-8 mx-auto text-muted-foreground/40 mb-2" />
                <p className="text-sm text-muted-foreground">No league activity for this player</p>
              </CardContent>
            </Card>
          ) : (
            activity.map((item) => (
              <Card key={item.id} className="bg-muted/20">
                <CardContent className="p-3 flex items-center gap-3">
                  <div className={cn(
                    "w-8 h-8 rounded-full flex items-center justify-center text-sm flex-shrink-0",
                    item.activity_type === 'add' && 'bg-success/20 text-success',
                    item.activity_type === 'drop' && 'bg-destructive/20 text-destructive',
                    item.activity_type === 'trade' && 'bg-primary/20 text-primary',
                  )}>
                    {item.activity_type === 'add' ? '➕' : item.activity_type === 'drop' ? '➖' : '🔄'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground capitalize">{item.activity_type}</p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(item.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>
      </Tabs>

      {player && (
        <AddDropDialog
          open={addDropOpen}
          onOpenChange={setAddDropOpen}
          addPlayer={isFreeAgent ? {
            id: player.id,
            full_name: player.full_name,
            position_abbreviation: player.position_abbreviation,
            mlb_team_abbreviation: player.mlb_team_abbreviation,
            headshot_url: player.headshot_url,
            player_status: player.status,
          } : null}
          dropPlayer={isOnMyTeam ? addDrop.getMyRosterPlayer(player.id) || null : null}
          roster={addDrop.roster}
          faabRemaining={addDrop.faabRemaining}
          processing={addDrop.processing}
          onConfirm={addDrop.executeAddDrop}
          onAdd={addDrop.executeAdd}
          onAddToIL={addDrop.executeAddToIL}
          onSubmitClaim={(pid, faabBid, dropPlayerId) => waiverClaims.submitClaim(pid, faabBid, dropPlayerId, undefined, addDrop.getWaiverWireId(player.id) || undefined)}
          waiverMode={waiverMode}
          isOnWaiverWire={addDrop.isOnWaiverWire(player.id)}
          waiverExpiresAt={addDrop.getWaiverExpiry(player.id)}
          checkPositionLimit={addDrop.checkPositionLimit}
          checkSlotEligibility={addDrop.checkSlotEligibility}
          isAddPlayerILEligible={isFreeAgent ? addDrop.isFreeAgentILEligible({ id: player.id, position_abbreviation: player.position_abbreviation, status: player.status }) : false}
          availableILSlots={addDrop.availableILSlots}
          openActiveSlots={addDrop.openActiveSlots}
          totalRosterSize={addDrop.totalRosterSize}
        />
      )}
    </div>
  );
};

// Filter scoring rules by player type using category
function filterRulesForPlayer(rules: ScoringRule[], isPitcher: boolean): ScoringRule[] {
  return rules.filter(r => {
    // Fielding and misc apply to everyone
    if (r.category === 'fielding' || r.category === 'misc') return true;
    if (isPitcher) return r.category === 'pitching';
    // Hitters get batting + baserunning (not pitching)
    return r.category === 'batting' || r.category === 'baserunning';
  });
}

// Map scoring rule stat → player_season_stats column name
// Uses seasonStats (full row from player_season_stats) as primary data source
function getStatValue(seasonStats: any, stat: string): number {
  if (!seasonStats) return 0;
  const map: Record<string, string> = {
    // Batting
    strikeouts: 'strikeouts',
    gidp: 'gidp',
    outs: '__derived_outs',
    hits: 'hits',
    doubles: 'doubles',
    triples: 'triples',
    home_runs: 'home_runs',
    rbi: 'rbi',
    runs_scored: 'runs_scored',
    walks_drawn: 'walks_drawn',
    stolen_bases: 'stolen_bases',
    caught_stealing: 'caught_stealing',
    hit_by_pitch: 'hit_by_pitch',
    // Pitching
    earned_runs: 'earned_runs',
    walks: 'walks_allowed',
    hr_allowed: 'home_runs_allowed',
    hits_allowed: 'hits_allowed',
    hit_batters: 'hit_batsmen',
    wild_pitches: 'wild_pitches',
    balks: 'balks',
    blown_saves: 'blown_saves',
    losses: 'losses',
    pitcher_strikeouts: 'pitcher_strikeouts',
    pitcher_win: 'pitcher_wins',
    pitcher_outs: '__derived_pitcher_outs',
    save: 'saves',
    holds: 'holds',
    // Fielding
    errors: 'errors',
    passed_balls: 'passed_balls',
    outfield_assist: 'outfield_assists',
  };

  const key = map[stat];
  if (!key) return 0;
  if (key === '__derived_outs') {
    return (seasonStats.at_bats || 0) - (seasonStats.hits || 0);
  }
  if (key === '__derived_pitcher_outs') {
    return Math.round((seasonStats.innings_pitched || 0) * 3);
  }
  return seasonStats[key] || 0;
}

export default MLBBetaPlayerDetail;
